/**
 * Predict Relayer API 服务
 * 用于 Safe 部署和交易执行
 *
 * 使用 @polymarket/builder-relayer-client SDK
 */

import { ethers } from 'ethers5';
import { createPublicClient, http } from 'viem';
import { polygon } from 'viem/chains';
import { RelayClient } from '@polymarket/builder-relayer-client';
import { BuilderConfig, type BuilderHeaderPayload } from '@polymarket/builder-signing-sdk';
import { CHAIN_RPC_URLS } from './rpc-utils';
import { apiClient } from './api';

// ============ 常量配置 ============

/** Polymarket 官方 Relayer API */
const POLYMARKET_RELAYER_URL = 'https://relayer-v2.polymarket.com';


/** Polygon RPC URL */
const POLYGON_RPC_URL = process.env.NEXT_PUBLIC_POLYGON_RPC_URL || CHAIN_RPC_URLS[137];

/** Viem 公共客户端（用于链上查询） */
const publicClient = createPublicClient({
  chain: polygon,
  transport: http(POLYGON_RPC_URL),
});

/** Safe 相关常量 */
const SAFE_INIT_CODE_HASH = '0x2bce2127ff07fb632d16c8347c4ebf501f4841168bed00d9e6ef715ddb6fcecf';
const SAFE_FACTORY_ADDRESS = '0xaacFeEa03eb1561C4e67d661e40682Bd20E3541b';

/** Polygon 链 ID */
const POLYGON_CHAIN_ID = 137;

// ============ 类型定义 ============

/** Relayer 交易结果 */
export interface RelayerTransactionResult {
  transactionId: string;
  state: string;
  transactionHash?: string;
  safeAddress?: string;
}

/** 简单交易类型（SDK 使用） */
export interface Transaction {
  to: string;
  data: string;
  value: string;
}

// ============ 工具函数 ============

/**
 * 派生 Safe 地址
 * 使用 Create2 从 EOA 确定性派生 Safe 地址
 */
export function deriveSafe(
  address: string,
  safeFactory: string = SAFE_FACTORY_ADDRESS
): string {
  const salt = ethers.utils.keccak256(
    ethers.utils.defaultAbiCoder.encode(['address'], [address])
  );
  return ethers.utils.getCreate2Address(safeFactory, salt, SAFE_INIT_CODE_HASH);
}

// ============ SDK 初始化 ============

/**
 * 创建 RelayClient 实例
 * 使用 remoteBuilderConfig 指向后端签名端点
 */
function createRelayClient(signer: ethers.Signer): RelayClient {
  // 注意：url 仅用于满足 BuilderConfig 构造函数必填参数，实际请求由下方重写的 generateBuilderHeaders 通过 apiClient 发出
  const builderConfig = new BuilderConfig({
    remoteBuilderConfig: {
      url: 'https://placeholder.local',
    },
  });

  // 重写 generateBuilderHeaders，通过 apiClient 发送请求（自动携带 token header）
  builderConfig.generateBuilderHeaders = async (method: string, path: string, body?: string, timestamp?: number): Promise<BuilderHeaderPayload | undefined> => {
    try {
      const res = await apiClient.post('/app/prediction/sign', { method, path, body, timestamp });
      return res as unknown as BuilderHeaderPayload;
    } catch (err) {
      console.error('[RelayClient] Builder sign request failed:', err);
      return undefined;
    }
  };

  // SDK 接受 ethers.Wallet 或 JsonRpcSigner
  return new RelayClient(
    POLYMARKET_RELAYER_URL,
    POLYGON_CHAIN_ID,
    signer as ethers.Wallet,
    builderConfig
  );
}

// ============ API 方法 ============

/**
 * 从链上获取 Safe 合约的 nonce
 * 直接查询链上状态，避免 Relayer nonce 不同步问题
 */
export async function getSafeNonceOnChain(safeAddress: string): Promise<string> {
  const provider = new ethers.providers.JsonRpcProvider(POLYGON_RPC_URL);
  const abi = ['function nonce() view returns (uint256)'];
  const contract = new ethers.Contract(safeAddress, abi, provider);
  const nonce = await contract.nonce();
  return nonce.toString();
}

/**
 * 获取 nonce（优先从链上获取）
 */
export async function getNonce(address: string, type: string): Promise<string> {
  // 对于 SAFE 类型，优先从链上获取 nonce 以确保准确性
  if (type === 'SAFE') {
    try {
      const safeAddress = deriveSafe(address);
      const onChainNonce = await getSafeNonceOnChain(safeAddress);
      console.log('[Relayer] Using on-chain nonce:', onChainNonce);
      return onChainNonce;
    } catch (error) {
      console.warn('[Relayer] Failed to get on-chain nonce, falling back to Relayer API:', error);
    }
  }

  // Fallback: 从 Polymarket 官方 Relayer API 获取（无需认证）
  const response = await fetch(
    `${POLYMARKET_RELAYER_URL}/nonce?address=${address}&type=${type}`
  );
  if (!response.ok) {
    throw new Error(`Failed to get nonce: ${response.statusText}`);
  }
  const data = await response.json();
  return data.nonce;
}

/**
 * 检查 Safe 是否已部署（链上查询）
 * 通过检查合约地址是否有代码来判断
 */
export async function checkSafeDeployed(safeAddress: string): Promise<boolean> {
  try {
    const code = await publicClient.getCode({
      address: safeAddress as `0x${string}`,
    });
    // 如果有代码（不是 undefined 或 '0x'），说明合约已部署
    return code !== undefined && code !== '0x';
  } catch (error) {
    console.error('[Relayer] Failed to check deployment on-chain:', error);
    return false;
  }
}

/**
 * 部署 Safe（使用 SDK）
 */
export async function deploySafe(
  signer: ethers.Signer
): Promise<RelayerTransactionResult> {
  const from = await signer.getAddress();
  const safeAddress = deriveSafe(from);

  // 检查是否已部署（链上查询）
  const deployed = await checkSafeDeployed(safeAddress);
  if (deployed) {
    throw new Error('Safe already deployed');
  }

  console.log('[Relayer] Deploying Safe via SDK:', safeAddress);

  // 创建 RelayClient 并调用 deploy
  const client = createRelayClient(signer);
  const response = await client.deploy();

  // 等待交易完成
  const result = await response.wait();
  if (!result) {
    throw new Error('Safe deployment failed or timeout');
  }

  console.log('[Relayer] Safe deployed:', safeAddress, 'txHash:', result.transactionHash);
  return {
    transactionId: result.transactionID,
    state: result.state,
    transactionHash: result.transactionHash,
    safeAddress: result.proxyAddress || safeAddress,
  };
}

/**
 * 执行 Safe 交易（使用 SDK）
 */
export async function executeSafeTransactions(
  signer: ethers.Signer,
  transactions: Transaction[],
  metadata?: string
): Promise<RelayerTransactionResult> {
  if (transactions.length === 0) {
    throw new Error('No transactions to execute');
  }

  console.log('[Relayer] Executing transactions via SDK:', transactions.length);

  // 创建 RelayClient 并调用 execute
  const client = createRelayClient(signer);
  const response = await client.execute(transactions, metadata);

  // 等待交易完成
  const result = await response.wait();
  if (!result) {
    throw new Error('Transaction execution failed or timeout');
  }

  console.log('[Relayer] Transactions executed:', result.transactionID, 'txHash:', result.transactionHash);
  return {
    transactionId: result.transactionID,
    state: result.state,
    transactionHash: result.transactionHash,
  };
}
